/**
 * Umwandlung HTML-Vorlage → Bausteine (Bestand, Konzept 3.10; weiterentwickelt aus apps/web/…/bloecke.ts parseBlocks).
 *
 * Grundsatz: rundlauftreu. Jeder erkannte Baustein wird sofort zurückübersetzt (compileBlock) und mit der Quelle Zeichen für
 * Zeichen verglichen; weicht er ab, bleibt genau dieser Ausschnitt „Freitext aus alter Vorlage“ (legacy_html). Damit gilt für
 * jede Vorlage: compileBlocks(convertTemplate(v).blocks) ergibt dieselben Felder wie v, also denselben gerenderten Text und
 * denselben Prüfwert. Für die Mustervorlagen entsteht dabei kein Freitext (Test: contract-blocks.test.ts).
 *
 * Erkannt werden: Überschriften h1–h4 (auch zentriert/rechts), Absätze, interne Hinweise, Aufzählungen mit bedingten Punkten,
 * Seitenumbrüche, schlichte Tabellen, Unterschriftsfelder, „Nur wenn … / Sonst“ um ganze Abschnitte (auch verschachtelt),
 * wählbare Klauseln (Bedingung auf eine Ja/Nein-Angabe mit ui = "choice") und im Text fett, kursiv, unterstrichen,
 * Zeilenumbrüche, Felder und „Nur wenn“ im Satz. Der vertrauliche Teil (annex_html) wird ein Baustein „private“.
 */
import {
  codePoints, DEFAULT_WS, defaultWs, fieldRefOf, validateBlocks,
  type Block, type ContainerKind, type FieldRef, type Inline, type InlineMark, type ListItem,
} from "./contract-blocks.js";
import { compileBlock, type ChoiceVariableDef } from "./contract-blocks-compile.js";
import { CONTRACT_LIMITS } from "./contract-markup.js";
import { CONTRACT_ROLES, isContractRole, type ContractRole, type CustomVariableDef } from "./contract-variables.js";
import type { Language } from "./types.js";

export interface ConvertInput {
  bodyHtml: string;
  annexHtml: string | null;
  annexHiddenFrom: readonly ContractRole[];
  variables?: readonly CustomVariableDef[];
  language: Language;
}

export interface ConvertResult {
  blocks: Block[];
  /** Anzahl der Freitext-Bausteine (legacy_html), die übrig blieben */
  legacy: number;
  /** Schema-Befund der Umwandlung (validateBlocks), null = gültig */
  problem: string | null;
}

const WS = /[ \t\r\n]/;
const SYSTEM_PATH = /^[a-z][a-z0-9_]*\.[a-z][a-z0-9_]*$/;
const CUSTOM_KEY = /^[a-z][a-z0-9_]{0,63}$/;
const FILTER = /^[a-z]{1,20}$/;
const ENTITIES: Record<string, string> = { amp: "&", lt: "<", gt: ">", quot: '"', apos: "'", nbsp: " ", "#39": "'" };

function decode(s: string): string {
  return s.replace(/&(amp|lt|gt|quot|apos|nbsp|#39);/g, (_, e: string) => ENTITIES[e] ?? _)
    .replace(/&#(\d{1,7});/g, (m, n: string) => (Number(n) <= 0x10ffff ? String.fromCodePoint(Number(n)) : m))
    .replace(/&#x([0-9a-f]{1,6});/gi, (m, n: string) => (parseInt(n, 16) <= 0x10ffff ? String.fromCodePoint(parseInt(n, 16)) : m));
}

/** Pfad aus einem Platzhalter als FieldRef, oder null, wenn er nicht dem Schema entspricht. */
function refOf(path: string): FieldRef | null {
  const r = fieldRefOf(path);
  return (r.src === "system" ? SYSTEM_PATH : CUSTOM_KEY).test(r.path) ? r : null;
}

type Tok =
  | { k: "open"; tag: string; attrs: string; raw: string }
  | { k: "close"; tag: string; raw: string }
  | { k: "ph"; inner: string; raw: string }
  | { k: "text"; v: string; raw: string };

const INLINE_TOKEN = /<(\/?)([a-zA-Z][a-zA-Z0-9]*)((?:[^>"']|"[^"]*"|'[^']*')*)>|\{\{([^{}]*)\}\}|([^<{]+|[<{])/g;

function tokens(html: string): Tok[] {
  const out: Tok[] = [];
  for (const m of html.matchAll(INLINE_TOKEN)) {
    if (m[2] !== undefined) out.push(m[1] ? { k: "close", tag: m[2].toLowerCase(), raw: m[0] } : { k: "open", tag: m[2].toLowerCase(), attrs: m[3] ?? "", raw: m[0] });
    else if (m[4] !== undefined) out.push({ k: "ph", inner: m[4], raw: m[0] });
    else out.push({ k: "text", v: m[5] ?? "", raw: m[0] });
  }
  return out;
}

const MARK_OF: Record<string, InlineMark> = { strong: "b", em: "i", u: "u" };

/** Inline-HTML in Läufe; null, wenn etwas vorkommt, das Läufe nicht genau tragen (dann bleibt der Baustein Freitext). */
export function parseInline(html: string): Inline[] | null {
  const root: Inline[] = [];
  let list = root;
  const marks: InlineMark[] = [];
  const frames: { node: Extract<Inline, { t: "if" }>; parent: Inline[]; inElse: boolean }[] = [];
  let pendingText = "";
  const flush = () => {
    if (pendingText === "") return;
    list.push(marks.length ? { t: "text", v: decode(pendingText), marks: [...marks] } : { t: "text", v: decode(pendingText) });
    pendingText = "";
  };
  for (const tk of tokens(html)) {
    if (tk.k === "text") { pendingText += tk.v; continue; }
    flush();
    if (tk.k === "open") {
      if (tk.tag === "br") { list.push(marks.length ? { t: "br", marks: [...marks] } : { t: "br" }); continue; }
      const mark = MARK_OF[tk.tag];
      if (!mark || tk.attrs.trim() !== "" || marks.includes(mark)) return null;
      marks.push(mark);
      continue;
    }
    if (tk.k === "close") {
      const mark = MARK_OF[tk.tag];
      if (!mark || marks[marks.length - 1] !== mark) return null;
      marks.pop();
      continue;
    }
    // Platzhalter
    const inner = tk.inner.trim();
    if (inner === "else" || inner === "/if") {
      const f = frames[frames.length - 1];
      if (!f || marks.length) return null;
      if (inner === "else") {
        if (f.inElse) return null;
        f.inElse = true;
        f.node.else = [];
        list = f.node.else;
      } else {
        frames.pop();
        list = f.parent;
      }
      continue;
    }
    if (/^#if\s+\S/.test(inner)) {
      const when = refOf(inner.slice(3).trim());
      if (!when || marks.length || frames.length >= CONTRACT_LIMITS.depth - 1) return null;
      const node: Extract<Inline, { t: "if" }> = { t: "if", when, then: [] };
      list.push(node);
      frames.push({ node, parent: list, inElse: false });
      list = node.then;
      continue;
    }
    if (inner.startsWith("signature:") || inner.startsWith("#")) return null;
    const parts = inner.split("|").map((p) => p.trim());
    const field = refOf(parts[0] ?? "");
    const filters = parts.slice(1);
    if (!field || filters.length > 5 || !filters.every((f) => FILTER.test(f))) return null;
    const node: Inline = { t: "field", field, ...(filters.length ? { filters } : {}), ...(marks.length ? { marks: [...marks] } : {}) };
    list.push(node);
  }
  flush();
  if (frames.length || marks.length) return null;
  return root;
}

// ---------------------------------------------------------------------------
// Blockebene
// ---------------------------------------------------------------------------

const PLACEHOLDER_AT = /^\{\{([^{}]*)\}\}/;
const OPEN_TAG_AT = /^<([a-zA-Z][a-zA-Z0-9]*)((?:[^>"']|"[^"]*"|'[^']*')*)>/;

type SeqStop = "else" | "endif" | "eof";

class Parser {
  private n = 0;
  legacy = 0;
  constructor(private readonly src: string, private readonly choices: ReadonlyMap<string, CustomVariableDef>, private readonly lang: Language) {}

  id(prefix: "b" | "i"): string {
    this.n += 1;
    return `${prefix}${this.n}`;
  }

  private legacyBlock(html: string): Block {
    this.legacy += 1;
    return { id: this.id("b"), type: "legacy_html", html };
  }

  /** Stimmt der Baustein zeichengenau mit der Quelle überein? Sonst Freitext. */
  private verified(block: Block, slice: string): Block {
    if (compileBlock(block, { language: this.lang, attachments: 0 }) === slice) return block;
    return this.legacyBlock(slice);
  }

  /** Folge von Bausteinen bis Ende, {{else}} oder {{/if}} (nur innerhalb einer Bedingung). */
  seq(pos: number, kind: ContainerKind, inCond: boolean, depth: number): { blocks: Block[]; trailing: string; stop: SeqStop; pos: number } {
    const blocks: Block[] = [];
    const src = this.src;
    for (;;) {
      const wsStart = pos;
      while (pos < src.length && WS.test(src[pos]!)) pos += 1;
      const ws = src.slice(wsStart, pos);
      if (pos >= src.length) return { blocks, trailing: ws, stop: "eof", pos };
      const ph = PLACEHOLDER_AT.exec(src.slice(pos, pos + 400));
      if (ph && inCond) {
        const inner = (ph[1] ?? "").trim();
        if (inner === "else" || inner === "/if") return { blocks, trailing: ws, stop: inner === "else" ? "else" : "endif", pos: pos + ph[0].length };
      }
      const r = this.one(pos, depth);
      this.place(blocks, r.block, ws, kind);
      pos = r.pos;
    }
  }

  /** Leerraum vor dem Baustein festhalten; zu langer Leerraum wird ein eigener Freitext-Baustein. */
  private place(blocks: Block[], block: Block, ws: string, kind: ContainerKind): void {
    if (codePoints(ws) > CONTRACT_LIMITS.ws) {
      const filler = this.legacyBlock(ws);
      if (defaultWs(kind, blocks.length) !== "") filler.ws = "";
      blocks.push(filler);
      ws = "";
    }
    if (ws !== defaultWs(kind, blocks.length)) block.ws = ws;
    blocks.push(block);
  }

  private one(pos: number, depth: number): { block: Block; pos: number } {
    const src = this.src;
    const rest = src.slice(pos);
    if (rest.startsWith("{{")) {
      const ph = PLACEHOLDER_AT.exec(rest);
      if (ph) {
        const inner = (ph[1] ?? "").trim();
        if (/^#if\s+\S/.test(inner)) return this.condition(pos, ph[0], inner.slice(3).trim(), depth);
        if (inner.startsWith("signature:")) return this.signatures(pos);
        if (inner === "else" || inner === "/if") return { block: this.legacyBlock(ph[0]), pos: pos + ph[0].length };
      }
      return this.textChunk(pos);
    }
    if (rest.startsWith("<!--")) {
      const end = src.indexOf("-->", pos + 4);
      const stop = end < 0 ? src.length : end + 3;
      return { block: this.legacyBlock(src.slice(pos, stop)), pos: stop };
    }
    if (rest.startsWith("</")) {
      const end = src.indexOf(">", pos);
      const stop = end < 0 ? src.length : end + 1;
      return { block: this.legacyBlock(src.slice(pos, stop)), pos: stop };
    }
    const open = OPEN_TAG_AT.exec(rest);
    if (open) return this.element(pos, open);
    return this.textChunk(pos);
  }

  /** Freier Text oder ein Feld auf Blockebene: bis zum nächsten Tag oder Steuer-Platzhalter, als Freitext. */
  private textChunk(pos: number): { block: Block; pos: number } {
    const rest = this.src.slice(pos);
    const next = rest.slice(1).search(/<|\{\{\s*(#if|else|\/if|signature:)/);
    const len = next < 0 ? rest.length : next + 1;
    return { block: this.legacyBlock(rest.slice(0, len)), pos: pos + len };
  }

  private element(pos: number, open: RegExpExecArray): { block: Block; pos: number } {
    const src = this.src;
    const tag = open[1]!.toLowerCase();
    const attrs = open[2] ?? "";
    const innerStart = pos + open[0].length;
    if (tag === "br" || tag === "hr") return { block: this.legacyBlock(open[0]), pos: innerStart };
    const close = findClose(src, open[1]!, innerStart);
    if (close < 0) return { block: this.legacyBlock(open[0]), pos: innerStart };
    const closeEnd = src.indexOf(">", close) + 1;
    const slice = src.slice(pos, closeEnd);
    const inner = src.slice(innerStart, close);
    const cls = /^\s+class="([^"]*)"\s*$/.exec(attrs)?.[1] ?? (attrs.trim() === "" ? "" : null);
    const align = cls === "tk-align-center" ? "center" : cls === "tk-align-right" ? "right" : null;
    let block: Block | null = null;
    if (/^h[1-4]$/.test(tag) && (cls === "" || align)) {
      const text = parseInline(inner);
      if (text) block = { id: this.id("b"), type: "heading", level: Number(tag[1]) as 1 | 2 | 3 | 4, text, ...(align ? { align } : {}) };
    } else if (tag === "p" && (cls === "" || align || cls === "tk-sample-notice")) {
      const text = parseInline(inner);
      if (text) block = cls === "tk-sample-notice" ? { id: this.id("b"), type: "note", text } : { id: this.id("b"), type: "paragraph", text, ...(align ? { align } : {}) };
    } else if ((tag === "ol" || tag === "ul") && cls === "") {
      block = this.list(inner, tag === "ol");
    } else if (tag === "div" && cls === "tk-page-break" && inner === "") {
      block = { id: this.id("b"), type: "pagebreak" };
    } else if (tag === "table" && cls === "") {
      block = this.table(inner);
    }
    return { block: block ? this.verified(block, slice) : this.legacyBlock(slice), pos: closeEnd };
  }

  private list(inner: string, ordered: boolean): Block | null {
    const items: ListItem[] = [];
    let pos = 0;
    for (;;) {
      const wsStart = pos;
      while (pos < inner.length && WS.test(inner[pos]!)) pos += 1;
      const ws = inner.slice(wsStart, pos);
      if (codePoints(ws) > CONTRACT_LIMITS.ws) return null;
      if (pos >= inner.length) {
        return { id: this.id("b"), type: "list", ordered, items, ...(ws !== DEFAULT_WS.listEnd ? { wsEnd: ws } : {}) };
      }
      const rest = inner.slice(pos);
      let when: FieldRef | undefined;
      let body = rest;
      const cond = /^\{\{#if ([^{}]+)\}\}/.exec(rest);
      if (cond) {
        const ref = refOf(cond[1]!.trim());
        if (!ref) return null;
        when = ref;
        body = rest.slice(cond[0].length);
      }
      if (!body.startsWith("<li>")) return null;
      const end = findClose(body, "li", 4);
      if (end < 0) return null;
      const liInner = body.slice(4, end);
      if (/<(ol|ul|li)\b/i.test(liInner)) return null;
      const text = parseInline(liInner);
      if (!text) return null;
      let used = (cond ? cond[0].length : 0) + end + "</li>".length;
      if (cond) {
        if (!rest.slice(used).startsWith("{{/if}}")) return null;
        used += "{{/if}}".length;
      }
      const item: ListItem = { id: this.id("i"), text, ...(when ? { when } : {}) };
      if (ws !== DEFAULT_WS.item) item.ws = ws;
      items.push(item);
      pos += used;
      if (items.length > CONTRACT_LIMITS.items) return null;
    }
  }

  private table(inner: string): Block | null {
    const cells = (row: string, tag: "td" | "th"): Inline[][] | null => {
      const out: Inline[][] = [];
      const re = new RegExp(`<${tag}>([\\s\\S]*?)</${tag}>`, "gy");
      let m: RegExpExecArray | null;
      let at = 0;
      while ((m = re.exec(row)) !== null) {
        const c = parseInline(m[1] ?? "");
        if (!c) return null;
        out.push(c);
        at = re.lastIndex;
      }
      return at === row.length && out.length ? out : null;
    };
    const m = /^(?:<thead><tr>([\s\S]*?)<\/tr><\/thead>)?<tbody>([\s\S]*)<\/tbody>$/.exec(inner);
    if (!m) return null;
    const header = m[1] !== undefined ? cells(m[1], "th") : undefined;
    if (header === null) return null;
    const rows: Inline[][][] = [];
    const rowRe = /<tr>([\s\S]*?)<\/tr>/y;
    let at = 0;
    let r: RegExpExecArray | null;
    const body = m[2] ?? "";
    while ((r = rowRe.exec(body)) !== null) {
      const c = cells(r[1] ?? "", "td");
      if (!c) return null;
      rows.push(c);
      at = rowRe.lastIndex;
    }
    if (at !== body.length) return null;
    return { id: this.id("b"), type: "table", ...(header ? { header } : {}), rows };
  }

  /** Aufeinanderfolgende Unterschriftsfelder mit gleichem Abstand werden ein Baustein. */
  private signatures(pos: number): { block: Block; pos: number } {
    const src = this.src;
    const first = PLACEHOLDER_AT.exec(src.slice(pos))!;
    const role = first[1]!.trim().slice(10).trim();
    if (!isContractRole(role) || first[1] !== `signature:${role}`) return { block: this.legacyBlock(first[0]), pos: pos + first[0].length };
    const parties: ContractRole[] = [role];
    let sep: string | null = null;
    let at = pos + first[0].length;
    for (;;) {
      let p = at;
      while (p < src.length && WS.test(src[p]!)) p += 1;
      const w = src.slice(at, p);
      const next = PLACEHOLDER_AT.exec(src.slice(p, p + 40));
      const r = next ? next[1]! : "";
      if (!next || !r.startsWith("signature:") || !isContractRole(r.slice(10)) || parties.includes(r.slice(10) as ContractRole)) break;
      if ((sep === null && codePoints(w) > CONTRACT_LIMITS.ws) || (sep !== null && w !== sep)) break;
      sep ??= w;
      parties.push(r.slice(10) as ContractRole);
      at = p + next[0].length;
    }
    const block: Block = { id: this.id("b"), type: "signatures", parties, ...(sep !== null && sep !== DEFAULT_WS.sep ? { sep } : {}) };
    return { block: this.verified(block, src.slice(pos, at)), pos: at };
  }

  /** „Nur wenn …“ um ganze Abschnitte (mit Sonst); ist die Struktur offen, bleibt nur der Steuer-Platzhalter Freitext. */
  private condition(pos: number, token: string, path: string, depth: number): { block: Block; pos: number } {
    const start = pos;
    const legacyBefore = this.legacy;
    const fallback = (html: string, next: number) => {
      // verworfene Kinder zählen nicht als Freitext; der ganze Ausschnitt ist ein Freitext-Baustein
      this.legacy = legacyBefore;
      return { block: this.legacyBlock(html), pos: next };
    };
    const when = refOf(path);
    const then = this.seq(pos + token.length, "cond", true, depth + 1);
    let otherwise: ReturnType<Parser["seq"]> | null = null;
    let end = then.pos;
    let ok = then.stop !== "eof";
    if (then.stop === "else") {
      otherwise = this.seq(then.pos, "cond", true, depth + 1);
      ok = otherwise.stop === "endif";
      end = otherwise.pos;
    }
    // Offene Bedingung: nur der Steuer-Platzhalter bleibt Freitext, der Rest wird danach normal gelesen
    if (!ok) return fallback(token, pos + token.length);
    const slice = this.src.slice(start, end);
    const wsTooLong = [then.trailing, otherwise?.trailing ?? ""].some((w) => codePoints(w) > CONTRACT_LIMITS.ws);
    if (!when || depth >= CONTRACT_LIMITS.depth || wsTooLong) return fallback(slice, end);
    const wsEnd = otherwise ? otherwise.trailing : then.trailing;
    // Wählbare Klausel: Bedingung auf eine Ja/Nein-Angabe mit ui = "choice", ohne Sonst
    const choice = when.src === "custom" && !otherwise ? this.choices.get(when.path) : undefined;
    let block: Block;
    if (choice) {
      const c = choice as ChoiceVariableDef;
      block = {
        id: this.id("b"), type: "choice", key: when.path, question: { de: c.label.de, en: c.label.en }, default: c.default === true,
        ...(c.help ? { help: { de: c.help.de, en: c.help.en } } : {}), children: then.blocks,
        ...(wsEnd !== DEFAULT_WS.end.cond ? { wsEnd } : {}),
      };
    } else {
      block = {
        id: this.id("b"), type: "condition", when, children: then.blocks,
        ...(otherwise ? { otherwise: otherwise.blocks } : {}),
        ...(otherwise && then.trailing !== DEFAULT_WS.else ? { wsElse: then.trailing } : {}),
        ...(wsEnd !== DEFAULT_WS.end.cond ? { wsEnd } : {}),
      };
    }
    if (compileBlock(block, { language: this.lang, attachments: 0 }) !== slice) return fallback(slice, end);
    return { block, pos: end };
  }
}

/** Passendes schließendes Tag unter Beachtung gleichnamiger Verschachtelung; -1, wenn keins. */
function findClose(html: string, tag: string, from: number): number {
  const re = new RegExp(`<(/?)${tag}\\b[^>]*>`, "gi");
  re.lastIndex = from;
  let depth = 1;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html)) !== null) {
    depth += m[1] ? -1 : 1;
    if (depth === 0) return m.index;
  }
  return -1;
}

/** Eine Wahlklausel-Angabe ist nur dann eine Wahlklausel, wenn der Übersetzer sie genau so wieder erzeugt. */
function choiceDefs(vars: readonly CustomVariableDef[] | undefined): Map<string, CustomVariableDef> {
  const out = new Map<string, CustomVariableDef>();
  for (const v of vars ?? []) {
    const c = v as Partial<ChoiceVariableDef>;
    if (c.ui !== "choice" || v.type !== "boolean") continue;
    const regenerated: Record<string, unknown> = {
      key: v.key, label: { de: v.label.de, en: v.label.en }, type: "boolean", required: false, default: v.default === true, ui: "choice",
      ...(c.help ? { help: { de: c.help.de, en: c.help.en } } : {}),
    };
    if (JSON.stringify(sortKeys(regenerated)) === JSON.stringify(sortKeys(v as unknown as Record<string, unknown>))) out.set(v.key, v);
  }
  return out;
}

function sortKeys(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(sortKeys);
  if (v && typeof v === "object") return Object.fromEntries(Object.keys(v).sort().map((k) => [k, sortKeys((v as Record<string, unknown>)[k])]));
  return v;
}

/**
 * Bestandsvorlage in Bausteine umwandeln. Der Hauptteil wird zur Liste der Bausteine, ein vorhandener vertraulicher Teil
 * (annex_html, auch leer) zum letzten Baustein „private“ mit visibleTo = alle Rollen außer annex_hidden_from.
 */
export function convertTemplate(input: ConvertInput): ConvertResult {
  const choices = choiceDefs(input.variables);
  const body = new Parser(input.bodyHtml ?? "", choices, input.language);
  const main = body.seq(0, "body", false, 1);
  const blocks = main.blocks;
  if (main.trailing !== "") {
    // Leerraum am Ende des Hauptteils (kommt in den Mustern nicht vor): als leerer Freitext mit genau diesem Abstand
    const tail: Block = { id: body.id("b"), type: "legacy_html", html: "" };
    body.legacy += 1;
    if (codePoints(main.trailing) <= CONTRACT_LIMITS.ws) {
      tail.ws = main.trailing;
    } else {
      tail.html = main.trailing;
      tail.ws = "";
    }
    blocks.push(tail);
  }
  let legacy = body.legacy;
  if (input.annexHtml !== null && input.annexHtml !== undefined) {
    // eigene Kennungen im vertraulichen Teil: p-Präfix über den Zähler des Hauptteils hinaus
    const annex = new Parser(input.annexHtml, choices, input.language);
    const part = annex.seq(0, "annex", false, 2);
    const children = relabel(part.blocks, body);
    let wsEnd = part.trailing;
    if (codePoints(wsEnd) > CONTRACT_LIMITS.ws) {
      children.push({ id: body.id("b"), type: "legacy_html", html: wsEnd, ws: "" });
      annex.legacy += 1;
      wsEnd = "";
    }
    blocks.push({
      id: body.id("b"), type: "private", visibleTo: CONTRACT_ROLES.filter((r) => !input.annexHiddenFrom.includes(r)),
      children, ...(wsEnd !== DEFAULT_WS.end.annex ? { wsEnd } : {}),
    });
    legacy += annex.legacy;
  }
  return { blocks, legacy, problem: validateBlocks(blocks) };
}

/** Kennungen eines zweiten Parsers auf den Zähler des ersten umstellen (eindeutig im ganzen Dokument). */
function relabel(blocks: Block[], ids: Parser): Block[] {
  const visit = (list: Block[]) => {
    for (const b of list) {
      b.id = ids.id("b");
      if (b.type === "list") for (const it of b.items) it.id = ids.id("i");
      if (b.type === "condition") { visit(b.children); if (b.otherwise) visit(b.otherwise); }
      if (b.type === "choice" || b.type === "private" || b.type === "attachment" || b.type === "clause") visit(b.children);
    }
  };
  visit(blocks);
  return blocks;
}
